/**
 * Passwordless passkey login for the patient portal (CU-86ajzr94n, sub-task 3).
 *
 * The logged-out half of the passkey work, and the highest-risk piece: it mints
 * an authenticated session from a surface with no session at all.
 *
 *   challenge(authenticate) → browser ceremony → verify → signInWithCustomToken
 *
 * After the token exchange resolves, `onAuthStateChanged` in `AuthContext`
 * fires and sets `currentUser`, exactly as it does for the password path. This
 * hook deliberately does NOT navigate — `login.tsx` owns routing, and its
 * existing nav effect consumes `requestedURL` unchanged.
 *
 * ── TWO WAYS IN, ONE CEREMONY ────────────────────────────────────────────────
 *
 *   autofill:  conditional mediation. Credentials appear in the browser's own
 *              autofill popup on the email field. Nothing is shown when the
 *              store is empty, and nothing interrupts a patient typing a
 *              password.
 *   button:    a visible "Sign in with a passkey" control, for browsers with no
 *              conditional-mediation support and for patients who prefer it.
 *
 * Both route through `loginWithPasskey` so there is ONE place a session is
 * minted, rather than two paths that can drift apart.
 *
 * ⚠️ THE PASSWORD PATH IS PERMANENTLY VISIBLE AND UNGATED, and SMS stays live
 * alongside it. This hook can fail in ANY way at all without stranding a
 * patient, and every outcome below is designed on that assumption. The argument
 * is stronger on the portal than it was on the console: patients are a far more
 * device-diverse population than three admins.
 *
 * ⚠️ SEAM DISCIPLINE. This hook imports from `services/passkey/passkeyFlows` and
 * NOTHING ELSE under `services/passkey/`. It must never reach `passkeyClient` or
 * `webPasskeyProvider`: exactly one file in this repo imports
 * `@simplewebauthn/browser`, and that invariant is held by the layering rather
 * than by anyone remembering it.
 *
 * ── PORTED FROM THE ADMIN CONSOLE, WITH ITS COMMENTS RE-DERIVED ──────────────
 *
 * Source: `USACS-MIDEO-Admin-Web-v2/src/hooks/usePasskeyLogin.ts` at `fd152fc7`
 * (the post-adversarial version). Ported wholesale on purpose — this is one
 * place where copying beats re-deriving, because the console's version survived
 * an adversarial round of 6 findings plus 2 more on re-verification.
 *
 * ⚠️ BUT ITS COMMENTS WERE AUDITED RATHER THAN COPIED (CU-86ajzr94n deliverable
 * 1), because a comment is a claim about enforcement and can be false. Each
 * load-bearing claim below was mutation-tested against the CONSOLE suite before
 * this file was written. Results are recorded inline, including the two that
 * turned out NOT to be enforced.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { useAuth } from '../contexts/AuthContext';
import { loginWithPasskey, passkeyAutofillAvailable, cancelPasskeyCeremony } from '../services/passkey/passkeyFlows';

/**
 * How long before the server's challenge TTL we re-arm the conditional request.
 *
 * ⚠️ THE PROBLEM THIS SOLVES IS SPECIFIC TO CONDITIONAL MEDIATION. The server
 * challenge lives 60s (`CHALLENGE_TTL_MS`) and a MODAL ceremony finishes well
 * inside that. A conditional request, by design, sits idle on the login screen
 * until the patient interacts with the email field — which may be minutes later
 * or never. Left alone it silently expires: the passkey still appears in the
 * autofill popup, the patient picks it, and the assertion is rejected against a
 * dead challenge with the server's uniform error — a failure indistinguishable
 * from a revoked credential.
 *
 * So the request is re-armed on a timer strictly SHORTER than the TTL. The
 * margin covers clock skew and the round trip for the replacement challenge.
 *
 * ⚠️ "STRICTLY INSIDE THE TTL" DOES NOT HOLD ON A BACKGROUNDED TAB, AND IS
 * RECORDED RATHER THAN FIXED. Browsers throttle `setInterval` in a background
 * tab to roughly once per minute, while this interval is 50s — so a login screen
 * left in a background tab drifts past the 60s challenge TTL and the armed
 * request goes stale.
 *
 * The consequence is benign and self-correcting: the stale request fails as
 * `failed`, which the conditional path handles SILENTLY (no error is shown), and
 * the next tick re-arms with a fresh challenge. Chasing it would mean a
 * `visibilitychange` listener re-arming on focus, which adds a lifecycle for a
 * failure the patient never sees.
 *
 * Stated here because the claim above would otherwise read as unconditional.
 * NOT verifiable by unit test — jsdom does not model background-tab throttling.
 */
const REARM_MARGIN_MS = 10_000;

/** Floor on the re-arm interval, so a small/absent server TTL cannot busy-loop. */
const MIN_REARM_INTERVAL_MS = 5_000;

/**
 * Compute the re-arm interval from the server's own TTL rather than hardcoding
 * 50s, so a server-side TTL change cannot silently desynchronise the client.
 *
 * ✅ PINNED. A test asserts the DERIVATION (`rearmIntervalMs(20_000)`), not just
 * the value at the current TTL — a test that only checked `=== 50_000` would
 * pass against a hardcoded constant, which is exactly the substitution this
 * function exists to prevent.
 */
export function rearmIntervalMs(challengeTtlMs: number): number {
  return Math.max(MIN_REARM_INTERVAL_MS, challengeTtlMs - REARM_MARGIN_MS);
}

/** The server challenge TTL, mirrored from `CHALLENGE_TTL_MS` in cloud-functions. */
export const CHALLENGE_TTL_MS = 60_000;

/**
 * Why a passkey login did not complete.
 *
 * Discriminated rather than collapsed to a boolean, on the same principle the
 * rest of this workstream applies: a member exists when THE PATIENT'S NEXT STEP
 * DIFFERS. `network` tells them to reconnect; `no-credentials` tells them this
 * browser holds nothing for this domain (the ordinary first-run case, not an
 * error); `failed` is everything else.
 *
 * ⚠️ NOTHING HERE DISTINGUISHES "no such credential" FROM "signature rejected".
 * The server returns one uniform error for every failure so a logged-out caller
 * cannot probe what an account holds. A revoked credential therefore arrives as
 * `failed`, and the copy behind it must not assert a reason.
 */
export type PasskeyLoginError = { kind: 'no-credentials' } | { kind: 'network' } | { kind: 'unsupported' } | { kind: 'failed' };

export interface UsePasskeyLoginResult {
  /** True while a MODAL ceremony or its token exchange is in flight. */
  busy: boolean;
  /** Set when a login attempt did not complete. Cleared on the next attempt. */
  error: PasskeyLoginError | null;
  /**
   * Whether to render the visible passkey button. False when WebAuthn is
   * unavailable — but NEVER a gate on the password path, which renders
   * regardless.
   */
  supported: boolean;
  /** Start a MODAL ceremony. Wired to the visible button. */
  signInWithPasskey: () => Promise<void>;
  /** Clear the current error (e.g. when the patient edits the email field). */
  clearError: () => void;
  /**
   * Arm conditional mediation. **The caller must invoke this only once an input
   * carrying `autocomplete="… webauthn"` is actually in the DOM.**
   *
   * ⚠️ ADVERSARIAL ROUND 2, F1 — THE HOOK USED TO ARM ITSELF ON MOUNT, AND THAT
   * IS DEAD ON ARRIVAL ON THIS SCREEN. `startAuthentication({ useBrowserAutofill:
   * true })` runs a precondition first:
   *
   *     document.querySelectorAll("input[autocomplete$='webauthn']")
   *
   * guarded by `verifyBrowserAutofillInput`, which DEFAULTS TO TRUE (confirmed
   * in the installed bundle: `verifyBrowserAutofillInput:d=!0`) and which the
   * seam never overrides. With no matching input it THROWS "Browser does not
   * support WebAuthn autofill".
   *
   * The portal's email field lives behind the `useEmailPassword` toggle, so at
   * hook-mount it is not in the DOM. The mount-time request therefore threw,
   * and the conditional path swallows every failure silently BY DESIGN — so the
   * feature was invisible with nothing to see in the UI, and the patient got no
   * autofill until the next re-arm tick, up to 50s later. Precisely while they
   * were typing.
   *
   * Arming is therefore the CALLER'S decision, because only the caller knows
   * when the field exists. Idempotent: safe to call on every render of the
   * field.
   */
  armConditional: () => void;
}

/**
 * Injection seam for tests, matching `usePasskeyEnrollment`'s convention.
 * Production passes nothing and every call goes to the real SDK / flows, so the
 * ceremony under test is the real one.
 */
export interface PasskeyLoginDeps {
  login?: (_options: { conditional?: boolean }) => Promise<Awaited<ReturnType<typeof loginWithPasskey>>>;
  autofillAvailable?: () => Promise<boolean>;
  cancelCeremony?: () => void;
  signIn?: (_customToken: string) => Promise<unknown>;
}

/**
 * @param onSuccess Called after the Firebase session is established. Kept as a
 *   callback rather than navigating here so this hook stays free of routing —
 *   the same split `usePasskeyEnrollment` uses.
 */
export function usePasskeyLogin(onSuccess?: () => void, deps: PasskeyLoginDeps = {}): UsePasskeyLoginResult {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<PasskeyLoginError | null>(null);
  const [supported, setSupported] = useState(false);

  /**
   * Guards against a resolved ceremony writing state into an unmounted
   * component. A conditional request can outlive the screen by minutes.
   */
  const mountedRef = useRef(true);

  /**
   * True while a MODAL ceremony is running. The conditional re-arm loop checks
   * this and stands down — re-arming mid-modal would abort the very ceremony
   * the patient is looking at, because the library cancels any in-flight
   * request when a new one starts.
   */
  const modalActiveRef = useRef(false);

  /**
   * Set once a login has succeeded, so neither the re-arm timer nor a late
   * ceremony can start another one while the session is being established.
   */
  const completedRef = useRef(false);

  /**
   * Whether conditional mediation was actually available on this browser.
   *
   * ⚠️ THE RE-ARM TIMER MUST CHECK THIS. Without it the timer would fire
   * conditional requests on browsers that do not support autofill, where the
   * library throws 'Browser does not support WebAuthn autofill' — producing a
   * `failed` outcome and a Sentry capture every interval, forever, on the login
   * screen. Silent on the UI (the conditional path never sets an error) and
   * therefore exactly the kind of fault that only shows up as noise in Sentry.
   *
   * ✅ PINNED INDIVIDUALLY — measured against the console suite: removing this
   * guard alone fails 1 test (`tsc` clean). Re-pinned here by
   * "does NOT re-arm when autofill is unavailable".
   */
  const autofillArmedRef = useRef(false);

  /**
   * Whether the browser can do conditional mediation at all, as answered by the
   * async probe. Distinct from `autofillArmedRef`, which means "we have
   * actually started a conditional request" — F1 conflated the two, and that
   * conflation is what let arming happen before any input existed.
   */
  const autofillCapableRef = useRef(false);

  /**
   * Whether the caller has told us an autofill-eligible input is mounted.
   *
   * Kept because the probe is ASYNC: the field can mount and call
   * `armConditional()` before `passkeyAutofillAvailable()` resolves. Without
   * this the arming request would simply be lost whenever the field won that
   * race, which is the common case on a fast machine.
   */
  const armRequestedRef = useRef(false);

  /**
   * True from the moment a login attempt starts until it fully settles.
   *
   * ⚠️ ADVERSARIAL FINDING F3 (console). `completedRef` alone was NOT
   * sufficient: it was checked on entry but only set AFTER the ceremony
   * resolved, leaving the latch open across the entire ceremony — which is the
   * longest part of the flow and, for a conditional request armed on mount, the
   * screen's NORMAL IDLE STATE.
   *
   * The concrete race: conditional armed → patient presses the button → modal
   * succeeds → the stale conditional then resolves `verified` too. Two
   * `signInWithCustomToken` calls and `onSuccess` twice. Double-clicking the
   * button produced the same thing.
   *
   * Not an authorisation bypass — both tokens were server-authorised for the
   * same account — but a redundant single-use challenge and rate-limiter slot
   * burned, and a UI state with no model behind it.
   *
   * ⚠️ DELIBERATELY SEPARATE FROM `completedRef`. They answer different
   * questions: `inFlightRef` is "is an attempt running right now" (cleared in
   * `finally`, so a retry is possible), `completedRef` is "has a session been
   * established" (never cleared on success, so nothing can follow it).
   *
   * ✅ PINNED — measured: replacing the asymmetric entry guard with a bare
   * `if (inFlightRef.current) return;` fails 2 console tests (`tsc` clean).
   */
  const inFlightRef = useRef(false);

  /**
   * Monotonic id for each login attempt.
   *
   * ⚠️ WITHOUT THIS THE LATCH IS WORSE THAN NONE. When a modal attempt
   * supersedes an idle conditional one, BOTH are running: the conditional
   * ceremony is aborted and resolves `cancelled` shortly after. Its `finally`
   * would then clear `inFlightRef` — which the MODAL attempt now owns —
   * silently reopening the very window F3 is about.
   *
   * So a run only clears the latch, or writes state, if it is still the current
   * one. Same reasoning as `mountedRef`, one scope narrower.
   *
   * ✅ THIS CLAIM WAS VERIFIED, NOT INHERITED. The task description flagged
   * "WORSE THAN NONE" as a strong claim nobody had confirmed. Measured against
   * the console suite: removing the `runId === runIdRef.current` guard from the
   * `finally` fails 9 tests with `tsc` clean. The claim holds as written.
   */
  const runIdRef = useRef(0);

  /**
   * `onSuccess` held in a ref so it does NOT participate in `runLogin`'s
   * identity.
   *
   * ⚠️ THIS IS A FIX FOR A MEASURED DEFECT, NOT A STYLE PREFERENCE. Callers pass
   * an inline arrow, which is a NEW FUNCTION ON EVERY RENDER. With `onSuccess`
   * in the dependency array, `runLogin` is rebuilt each render and both effects
   * below — which depend on `runLogin` — tear down and re-run.
   *
   * Two consequences, both invisible to unit tests and BOTH OBSERVED ON THE
   * CONSOLE'S STAGING:
   *   1. THREE `passkeyAuth` challenge calls on mount instead of one (measured
   *      at +0ms, +2ms, +282ms with no interaction), each burning a
   *      server-issued single-use challenge and a rate-limiter slot.
   *   2. Worse — the re-arm `setInterval` was CLEARED AND RECREATED on every
   *      render. A screen that re-renders more often than the interval would
   *      never re-arm at all, which is precisely the silent challenge-expiry
   *      this design exists to prevent.
   *
   * A ref keeps the latest callback without making it part of the identity.
   * This is the portal's instance of the *unstable callback identity* family;
   * `MfaChallengeForm`'s double-fired `onComplete` (CU-86ajzr8x1) was another.
   */
  const onSuccessRef = useRef(onSuccess);
  useEffect(() => {
    onSuccessRef.current = onSuccess;
  }, [onSuccess]);

  /**
   * The context's custom-token sign-in, held in a ref for the SAME reason as
   * `onSuccess` — `AuthContext` rebuilds its `value` object on every render, so
   * every member it exposes has a fresh identity each time. Putting this
   * straight into `establishSession`'s dependency array would rebuild
   * `runLogin`, and with it both effects below, on every render — the exact
   * triple-request-on-mount and never-re-arming timer recorded above.
   *
   * ⚠️ F2 (adversarial round 2). This is the ONE place the portal mints a
   * passkey session, and it goes through the single audited auth entry point
   * rather than reaching for the SDK a second time.
   */
  const { firebaseSignInWithCustomToken } = useAuth();
  const signInRef = useRef(firebaseSignInWithCustomToken);
  useEffect(() => {
    signInRef.current = firebaseSignInWithCustomToken;
  }, [firebaseSignInWithCustomToken]);

  /**
   * The deps object is held in a ref for the same reason as `onSuccess`: a
   * caller that passes an object literal would otherwise give `runLogin` a new
   * identity on every render and reintroduce both defects above. Production
   * passes nothing, so this is defence for the test seam rather than for the
   * shipped path — but a seam that can only be used unsafely is a trap.
   */
  const depsRef = useRef(deps);
  useEffect(() => {
    depsRef.current = deps;
  }, [deps]);

  const clearError = useCallback(() => setError(null), []);

  /**
   * Exchange a verified custom token for a Firebase session.
   *
   * ⚠️ SEPARATED FROM THE CEREMONY DELIBERATELY. This is the step that actually
   * mints the session, and it is the one place a failure means "the passkey was
   * accepted but the sign-in did not happen" — a state the patient cannot see
   * and would otherwise read as a rejected passkey. It gets its own capture.
   *
   * ⚠️ GOES THROUGH `AuthContext.firebaseSignInWithCustomToken`, NOT THE RAW SDK
   * — adversarial round 2, F2. The first version of this hook called
   * `signInWithCustomToken(auth, …)` directly while this comment pointed at the
   * context member, so the context member was DEAD CODE and the comment
   * misdescribed the file's own architecture. The explicit approval to modify
   * safety-railed auth code was granted for that member; routing around it
   * spent the approval on nothing and put a second auth entry point in the
   * codebase.
   *
   * ⚠️ THE TOKEN IS NEVER DECODED HERE. A uid read from the token is
   * display-gating only, never a security decision. The portal persists
   * Firebase's verified uid via `onAuthStateChanged` and routes through the
   * `$patientId` UID-mismatch guard.
   */
  const establishSession = useCallback(async (customToken: string): Promise<boolean> => {
    try {
      const signIn = depsRef.current.signIn ?? signInRef.current;
      await signIn(customToken);
      return true;
    } catch (err) {
      // NOT a passkey failure — the assertion already verified server-side.
      // Distinct tags so this is separable in Sentry from ceremony failures.
      //
      // ⚠️ NO PHI AND NO SECRETS. The custom token is a bearer credential and
      // the email identifies a patient; neither may reach Sentry. Only the
      // error and these two static tags are sent.
      Sentry.captureException(err, {
        tags: { feature: 'passkey', step: 'login-session' },
      });
      return false;
    }
  }, []);

  /**
   * The shared tail for both entry points.
   *
   * `conditional` selects autofill vs modal; everything after the ceremony is
   * identical, which is the point — one place mints a session.
   */
  const runLogin = useCallback(
    async (conditional: boolean): Promise<void> => {
      // ⚠️ F3 — TWO LATCHES ANSWERING TWO DIFFERENT QUESTIONS.
      //
      // `completedRef` — a session exists. Nothing may follow it, ever.
      if (completedRef.current) return;

      // `inFlightRef` — an attempt is running. A SECOND MODAL attempt must not
      // start (that is the double-click case), but a modal attempt IS allowed to
      // supersede an idle CONDITIONAL one: the library aborts the conditional
      // ceremony when the modal starts, and superseding is exactly what the
      // button is for.
      //
      // ⚠️ GATING THE BUTTON ON THE BARE LATCH WOULD BREAK IT OUTRIGHT. The
      // conditional request is armed as soon as the email field mounts and sits
      // open indefinitely, so the latch is held almost all the time on this
      // screen — a bare check made the console's button a no-op that minted
      // ZERO sessions. Measured here too: `if (inFlightRef.current) return;`
      // fails 2 console tests. The pinning test asserts a SUCCESSFUL login
      // rather than only an absence, because "no double session" also passes
      // against a button that does nothing.
      //
      // So: block only when the in-flight attempt is of the same kind or
      // stronger. A modal supersedes a conditional; nothing supersedes a modal.
      if (inFlightRef.current && (conditional || modalActiveRef.current)) return;

      // The superseded conditional request will resolve as `cancelled` (its
      // ceremony is aborted) and must not then clear the latch out from under
      // this attempt — `runId` below is what makes that safe.
      inFlightRef.current = true;
      const runId = ++runIdRef.current;

      // Only the modal path shows a spinner. A conditional request is armed in
      // the background and must not make the form look busy while the patient
      // is typing a password.
      if (!conditional) {
        modalActiveRef.current = true;
        setBusy(true);
        setError(null);
      }

      try {
        const runCeremony = depsRef.current.login;
        const outcome = runCeremony
          ? await runCeremony({ conditional })
          : await loginWithPasskey(undefined, { conditional });

        if (!mountedRef.current) return;

        // ⚠️ F3 — A SUPERSEDED RUN STOPS HERE. If a newer attempt started while
        // this one was in the ceremony, this result belongs to a ceremony the
        // library has already aborted. Acting on it is what produced the second
        // `signInWithCustomToken` and the second `onSuccess`.
        if (runId !== runIdRef.current) return;

        switch (outcome.status) {
          case 'verified': {
            // ⚠️ SET BEFORE THE AWAIT, NOT AFTER. `establishSession` is a
            // network round trip; latching only after it resolved would leave
            // that whole window open to a second attempt.
            //
            // ⚠️ THE ONE PATH THAT OBSERVES THIS IS NARROW, which is why it went
            // unconstrained through two console rounds. The re-arm timer calls
            // with `conditional = true`, so `inFlightRef.current && conditional`
            // returns before `completedRef` is read; the button during a MODAL
            // run is refused by `inFlightRef && modalActiveRef`. Only the BUTTON
            // DURING A CONDITIONAL RUN'S EXCHANGE reaches this latch with
            // nothing in front of it — `modalActiveRef` is false there.
            //
            // ✅ PINNED, AND VERIFIED RATHER THAN INHERITED. Measured against
            // the console suite: moving this line after the await fails exactly
            // 1 test, `tsc` clean. The equivalent test is ported below.
            completedRef.current = true;
            const ok = await establishSession(outcome.customToken);
            if (!mountedRef.current) return;
            if (ok) {
              onSuccessRef.current?.();
            } else {
              // ⚠️ RELEASING THIS IS DELIBERATE AND IS NOT THE F3 WINDOW.
              // The assertion verified but the sign-in did not happen, so the
              // patient must be able to retry rather than face a dead screen.
              //
              // It is safe because `inFlightRef` — cleared in `finally`, i.e.
              // strictly AFTER this line — is what serialises attempts. This
              // release only permits a LATER attempt, never a CONCURRENT one.
              completedRef.current = false;
              setError({ kind: 'failed' });
            }
            return;
          }

          case 'cancelled':
            // ⚠️ BENIGN AND SILENT, AND THIS IS THE ROUTINE CASE.
            //
            // Every superseded conditional request lands here: the library
            // aborts the in-flight ceremony when a new one starts, and an
            // AbortError maps to `cancelled` through the provider. Pressing the
            // button, re-arming on the timer, and unmounting all produce it.
            //
            // A dismissal by the patient is also `cancelled` and is equally not
            // an error — the spec deliberately makes dismissal, timeout and an
            // empty store indistinguishable. Surfacing any of these as a
            // failure would put a red message on the login screen every time a
            // patient ignored the autofill popup.
            return;

          case 'no-credentials':
            // Reachable only via conditional mediation, which is why the member
            // was retained in the union through sub-task 1. It means "nothing
            // enrolled for this domain" — the ordinary first-run state, so it is
            // not shown as an error on the silent path.
            if (!conditional) setError({ kind: 'no-credentials' });
            return;

          case 'unsupported':
            if (!conditional) setError({ kind: 'unsupported' });
            return;

          case 'network':
            if (!conditional) setError({ kind: 'network' });
            return;

          case 'failed':
          default:
            // Includes a REVOKED credential and an EXPIRED challenge — both
            // arrive as the server's uniform error and cannot be told apart
            // here. The copy behind this must not claim a reason.
            if (!conditional) setError({ kind: 'failed' });
            return;
        }
      } finally {
        // ⚠️ F3 — CLEARED ON EVERY PATH, INCLUDING A THROW. If this leaked, a
        // single failed attempt would wedge the screen: the button would stop
        // working and the re-arm would never fire again, silently, with the
        // password path as the only remaining way in.
        //
        // ⚠️ ONLY THE CURRENT RUN MAY CLEAR THE LATCH — see `runIdRef`, whose
        // claim was re-measured for this port (9 console tests fail without
        // this guard). A superseded conditional request resolves `cancelled`
        // shortly after a modal attempt takes over; if it cleared these, it
        // would release a latch the modal attempt owns.
        if (runId === runIdRef.current) {
          inFlightRef.current = false;
          if (!conditional) {
            modalActiveRef.current = false;
            if (mountedRef.current) setBusy(false);
          }
        }
      }
    },
    // ⚠️ `onSuccess` AND `deps` DELIBERATELY ABSENT — both are read through refs.
    // Adding either back reintroduces the triple-request-on-mount and the
    // never-re-arming timer described above. `establishSession` is stable
    // (`useCallback` with an empty dependency list).
    [establishSession],
  );

  /** The visible button. Always a MODAL ceremony. */
  const signInWithPasskey = useCallback(async () => {
    // No explicit abort of the conditional request is needed — and issuing one
    // would be redundant. `startAuthentication()` assigns a fresh signal from
    // `WebAuthnAbortService`, which aborts the previous controller first, so
    // the conditional request is superseded by this call itself. There is
    // deliberately NO app-owned `AbortController`.
    await runLogin(false);
  }, [runLogin]);

  /**
   * Arm conditional mediation — called by the screen once an input carrying
   * `autocomplete="… webauthn"` is in the DOM. See `UsePasskeyLoginResult` for
   * why this is the caller's decision (F1).
   *
   * IDEMPOTENT. The field re-renders on every keystroke, so this runs often;
   * `autofillArmedRef` makes every call after the first a no-op. Without that
   * latch each keystroke would start a fresh ceremony, aborting the previous
   * one — the autofill popup would close as the patient typed.
   */
  const armConditional = useCallback(() => {
    armRequestedRef.current = true;
    // The probe has not answered yet — it will honour the request when it does.
    if (!autofillCapableRef.current) return;
    // Already armed, or a session already exists.
    if (autofillArmedRef.current || completedRef.current) return;
    autofillArmedRef.current = true;
    void runLogin(true);
  }, [runLogin]);

  // ── Support probe ──────────────────────────────────────────────────────────
  useEffect(() => {
    mountedRef.current = true;
    let cancelled = false;

    (async () => {
      try {
        // ⚠️ THE AUTOFILL PROBE IS ASYNC. A synchronous gate on it is
        // always-truthy (a Promise is truthy), which would arm conditional
        // mediation on browsers that cannot do it.
        const probe = depsRef.current.autofillAvailable;
        const canAutofill = probe ? await probe() : await passkeyAutofillAvailable();
        if (!cancelled && mountedRef.current) {
          // Autofill support implies WebAuthn support, so a true answer is
          // sufficient for the button. A false answer is AMBIGUOUS (either no
          // WebAuthn at all, or WebAuthn without conditional UI), so we still
          // offer the button and let the ceremony report `unsupported` — which
          // is strictly better than hiding the control from a browser that can
          // in fact run a modal ceremony.
          setSupported(true);
          // ⚠️ RECORDS CAPABILITY ONLY — IT DOES NOT ARM. Arming here is F1:
          // the email field is not in the DOM at hook-mount on this screen, so
          // the request would throw its precondition and be swallowed. The
          // caller arms via `armConditional()` once the field exists.
          autofillCapableRef.current = canAutofill;
          // A field may already be on screen and have asked to arm before this
          // async probe resolved; honour that request now rather than dropping
          // it, or arming would depend on which of the two won the race.
          if (canAutofill && armRequestedRef.current) {
            autofillArmedRef.current = true;
            void runLogin(true);
          }
        }
      } catch {
        // Probe failure must not remove the button; the ceremony reports
        // `unsupported` on its own if the browser genuinely cannot.
        if (!cancelled && mountedRef.current) setSupported(true);
      }
    })();

    return () => {
      cancelled = true;
      mountedRef.current = false;
      // ⚠️ THE ONE ABORT WE GENUINELY OWN. The library cancels a ceremony when
      // a NEW one starts, but never on teardown — without this, a conditional
      // request armed here outlives the login screen.
      const cancelCeremony = depsRef.current.cancelCeremony;
      if (cancelCeremony) cancelCeremony();
      else cancelPasskeyCeremony();
    };
  }, [runLogin]);

  // ── Re-arm the conditional request before the challenge expires ────────────
  useEffect(() => {
    const interval = rearmIntervalMs(CHALLENGE_TTL_MS);

    const timer = setInterval(() => {
      // Never re-arm on a browser where autofill was not available — see
      // `autofillArmedRef`. This is the guard that keeps the timer from
      // generating a permanent, UI-silent Sentry stream.
      // ✅ Pinned individually: removing it alone fails a test.
      if (!autofillArmedRef.current) return;

      // Stand down while a modal ceremony is up (re-arming would abort the
      // ceremony the patient is looking at) or once a session exists.
      //
      // ⚠️ THIS GUARD IS FULLY REDUNDANT ON EVERY REACHABLE PATH, AND THE
      // COMMENT SAYING OTHERWISE WAS WRONG — DELIVERABLE 1, MEASURED TWICE.
      //
      // The console ships this line with no note about what constrains it, and
      // its suite does not: deleting it leaves 43/43 green, `tsc` clean. That
      // much was expected. What was NOT expected is that it cannot be pinned
      // here either, and the first attempt to pin it produced a HOLLOW TEST that
      // passed against the mutant. Both halves are shadowed inside `runLogin`:
      //
      //   modalActiveRef half — while a modal is in flight, `inFlightRef` is
      //     true, so the timer's `runLogin(true)` is refused by
      //     `inFlightRef.current && conditional` before any of this is read.
      //     Once the modal SETTLES, `modalActiveRef` is false anyway.
      //   completedRef half — `runLogin` re-checks `completedRef` on entry, so
      //     the timer's copy never decides anything.
      //
      // Measured: removing this line alone → 44/44 green. Removing it AND
      // `runLogin`'s `completedRef` entry guard → 4 tests fail. So the PROPERTY
      // is well pinned; this LINE is not the thing pinning it.
      //
      // ✅ KEPT DELIBERATELY. It is free, it is correct under refactor, and it
      // is a cheap second line of defence if `runLogin`'s entry guards are ever
      // reordered. A surviving mutant with a documented structural reason is a
      // stronger artifact than a manufactured kill — and manufacturing one here
      // would mean driving the timer against internals nothing else can reach,
      // which pins the mutant rather than the property.
      if (modalActiveRef.current || completedRef.current) return;

      // ⚠️ COMMENT CORRECTED FOR THIS PORT — DELIVERABLE 1. Measured: removing
      // this line alone also leaves the console suite green at 43/43.
      //
      // Unlike the guard above, this one is NOT straightforwardly observable in
      // jsdom: the effect's cleanup calls `clearInterval`, so after unmount the
      // timer does not fire at all and there is no path for a post-unmount tick
      // to reach this line. Reaching it would mean firing a timer against an
      // unmounted tree, which pins the mutant rather than the property.
      //
      // ✅ KEPT DELIBERATELY, AND DOCUMENTED AS UNPINNED. It is free, correct
      // under refactor, and it is the guard that would matter if the cleanup
      // above were ever removed or the timer hoisted out of this effect. A
      // surviving mutant with a documented structural reason is a stronger
      // artifact than a manufactured kill.
      if (!mountedRef.current) return;

      // Starting a new conditional request supersedes the stale one through the
      // library's own abort, so there is nothing to cancel first.
      void runLogin(true);
    }, interval);

    return () => clearInterval(timer);
  }, [runLogin]);

  return { busy, error, supported, signInWithPasskey, clearError, armConditional };
}
